import { PlaceHolder } from "@/services/smsPlaceHolders/type";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { splitByPlaceholders } from "./sms";
import classes from "./sms.module.scss";

export type MessageEditorHandle = {
  insertTag: (tag: PlaceHolder) => void;
  insertText: (text: string) => void;
  setText: (text: string) => void;
};

type Props = {
  initialText: string;
  tags: PlaceHolder[];
  onChange: (text: string) => void;
  invalid?: boolean;
  describedBy?: string;
};

// Zero-width space: keeps a trailing line break visible in the editor; never part of the text.
const ZWSP = String.fromCharCode(0x200b);

// Plain text with {{Key}} placeholders, edited as text plus non-editable variable chips.
const MessageEditor = forwardRef<MessageEditorHandle, Props>(function MessageEditor({ initialText, tags, onChange, invalid, describedBy }, ref) {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const textRef = useRef(initialText);
  const renderedTags = useRef(0);

  const makeToken = useCallback((tag: PlaceHolder) => {
    const token = document.createElement("span");
    token.className = "sms-token";
    token.contentEditable = "false";
    token.dataset.key = tag.key ?? "";
    token.append(document.createTextNode(tag.value ?? ""));
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "sms-token-remove";
    remove.setAttribute("aria-label", `حذف ${tag.value ?? ""}`);
    remove.textContent = "×";
    token.append(remove);
    return token;
  }, []);

  const serialize = useCallback((node: Node): string => {
    let out = "";
    node.childNodes.forEach((child, index) => {
      if (child.nodeType === Node.TEXT_NODE) out += (child.nodeValue ?? "").split(ZWSP).join("");
      else if (child instanceof HTMLElement) {
        if (child.dataset.key) out += child.dataset.key;
        else if (child.tagName === "BR") out += "\n";
        else if (child.tagName === "DIV" || child.tagName === "P") out += (index > 0 ? "\n" : "") + serialize(child);
        else out += serialize(child);
      }
    });
    return out;
  }, []);

  const emit = useCallback(() => {
    if (!editorRef.current) return;
    textRef.current = serialize(editorRef.current);
    onChange(textRef.current);
  }, [onChange, serialize]);

  const render = useCallback((text: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.replaceChildren();
    for (const part of splitByPlaceholders(text, tags)) {
      editor.append("text" in part ? document.createTextNode(part.text) : makeToken(part.tag));
    }
    renderedTags.current = tags.length;
    savedRange.current = null;
  }, [makeToken, tags]);

  useEffect(() => {
    render(textRef.current);
    // Render once; later changes come from typing or the handle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Placeholders load after the page; turn {{Key}} text into chips once they arrive.
  useEffect(() => {
    if (tags.length && renderedTags.current === 0 && textRef.current.includes("{{")) render(textRef.current);
  }, [render, tags.length]);

  useEffect(() => {
    const save = () => {
      const selection = window.getSelection();
      if (!selection?.rangeCount || !editorRef.current) return;
      const range = selection.getRangeAt(0);
      if (editorRef.current.contains(range.commonAncestorContainer)) savedRange.current = range.cloneRange();
    };
    document.addEventListener("selectionchange", save);
    return () => document.removeEventListener("selectionchange", save);
  }, []);

  const insertNode = useCallback((node: Node) => {
    const editor = editorRef.current;
    if (!editor) return;
    let range = savedRange.current;
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    // A fragment empties on insert, so remember its last child to place the caret after it.
    const last = node instanceof DocumentFragment ? node.lastChild : node;
    range.deleteContents();
    range.insertNode(node);
    if (last) range.setStartAfter(last);
    range.collapse(true);
    savedRange.current = range.cloneRange();
    if (document.activeElement === editor) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    emit();
  }, [emit]);

  useImperativeHandle(ref, () => ({
    insertTag: (tag) => {
      const editor = editorRef.current;
      const fragment = document.createDocumentFragment();
      // Keep a space between the variable and whatever is right before it.
      if (editor) {
        const before = document.createRange();
        before.selectNodeContents(editor);
        const at = savedRange.current && editor.contains(savedRange.current.commonAncestorContainer) ? savedRange.current : null;
        if (at) before.setEnd(at.startContainer, at.startOffset);
        const preceding = before.toString().split(ZWSP).join("");
        if (preceding && !/\s$/.test(preceding)) fragment.append(" ");
      }
      fragment.append(makeToken(tag), " ");
      insertNode(fragment);
    },
    insertText: (text) => insertNode(document.createTextNode(text)),
    setText: (text) => {
      textRef.current = text;
      render(text);
      onChange(text);
    },
  }), [insertNode, makeToken, onChange, render]);

  return (
    <div
      ref={editorRef}
      className={classes.editable}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label="متن پیامک"
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      data-placeholder="متن پیامک را بنویسید…"
      onInput={emit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          // The zero-width space lets a trailing line break show; serialize() drops it.
          insertNode(document.createTextNode("\n" + ZWSP));
        }
      }}
      onPaste={(event) => {
        event.preventDefault();
        insertNode(document.createTextNode(event.clipboardData.getData("text/plain")));
      }}
      onClick={(event) => {
        const remove = (event.target as HTMLElement).closest(".sms-token-remove");
        if (remove) {
          remove.parentElement?.remove();
          emit();
        }
      }}
    />
  );
});

export default MessageEditor;
